import { Input, Tooltip, message } from "antd";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record";
import {
  fetchFiles,
  fetchNewProviderMessage,
  fetchRuns,
  fetchVoice,
} from "../api/fetch";
import {
  InputIcon,
  MessageButtons,
  MessageInput,
  RecordInput,
  VoiceInput,
  WavePattern,
} from "../styles/Chat.styled";
import { getNewSearchString } from "../util/util";
import { RootContext } from "../context/RootContext";

interface RecordProps {
  handleSaveChat: Function;
  handleChangeLoading: Function;
  // noCenter: boolean;
  id: string | null;
  handleThreadChange: Function;
  handleChangeError: Function;
  providerId: string;
}

const Record = forwardRef((props: RecordProps, ref) => {
  const micRef = useRef<any>(null);
  const [record, setRecord] = useState<any>();
  const [audio, setAudio] = useState<any>();
  const [inputType, setInputType] = useState("message"); // voice
  const [isTalking, setIsTalking] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [isCancel, setIsCancel] = useState(false);
  const [refChange, setRefChange] = useState(false);
  const { providerData } = useContext<any>(RootContext);
  const navigate = useNavigate();
  const {
    handleSaveChat,
    handleChangeLoading,
    id,
    handleThreadChange,
    handleChangeError,
    providerId,
  } = props;

  useEffect(() => {
    if (!micRef.current) return;
    const wavesurfer = WaveSurfer.create({
      container: micRef.current,
      waveColor: "#6289E9",
      progressColor: "#fff",
      height: 200,
      barWidth: 10,
      barRadius: 10,
    });

    const record = wavesurfer.registerPlugin(RecordPlugin.create() as any);
    record.on("record-end", async (blob: any) => {
      const recordedUrl = URL.createObjectURL(blob);
      const duration = await record.getDuration(blob);
      const audio = {
        url: recordedUrl,
        type: blob.type.split(";")[0].split("/")[1] || "webm",
        duration,
      };
      setAudio(audio);
    });
    setRecord(record);

    return () => {
      if (wavesurfer) wavesurfer.destroy();
    };
  }, [micRef, refChange]);

  //fetch voice api
  useEffect(() => {
    if (!audio || isCancel) return;
    handleChangeLoading(true);
    setLoading(true);
    fetchVoice(audio.url, id, providerData?.provider_id).then((res) => {
      setLoading(false);
      if (res && res.voice_message) {
        saveChat({ text: res.voice_message }, "user", "text");
      }
      if (res.res && res.res.return_values && !res.res.return_values.run_id) {
        saveChat({ text: res.return_values.output }, "assistant", "text");
      }
      if (res.res && res.res.return_values && res.res.return_values.run_id) {
        getRes(res.res);
      }
      if (!id && res.res && res.res.thread_id) {
        const newSearchString =
          getNewSearchString("thread_id", res.res.thread_id) || "";
        console.log(newSearchString);
        navigate(newSearchString, { replace: true });
      }
      if (!res || (res && res.errorCode === "500")) {
        handleChangeError(true);
      }
    });

    // saveChat(TemplateRes, "assistant", "text")
    setAudio(null);
  }, [audio]);

  // close voice
  const destroyRecord = () => {
    setIsTalking(false);
    record.stopRecording();
    // micRef.current.innerHTML = "";
    setRefChange(!refChange);
  };

  // start voice
  const openRecord = () => {
    setIsTalking(true);
    record.startRecording();
  };

  const saveChat = (val: any, role: any, type: any) => {
    handleChangeError(false);
    handleChangeLoading(true);
    if (!val) return;
    let newMessage = { type: type, value: val, role: role };
    handleSaveChat(newMessage);
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      setInputValue("");
      saveChat({ text: event.target.value }, "user", "text");
      handleSendMessage(event.target.value);
    }
  };
  useImperativeHandle(ref, () => ({
    handleClickMessage(val: any) {
      handleSendMessage(val);
    },
  }));

  const handleSendMessage = (val: any) => {
    handleChangeLoading(true);
    setLoading(true);
    fetchNewProviderMessage(val, id, providerId).then((res) => {
      setLoading(false);
      if (res && res.return_values) {
        getRes(res);
      }
      if (!id && res && res.thread_id) {
        handleThreadChange(true);
        const newSearchString =
          getNewSearchString("thread_id", res.thread_id) || "";
        console.log(newSearchString);
        navigate(newSearchString, { replace: true });
      }
      if (!res || (res && res.errorCode === "500")) {
        handleChangeError(true);
      }
    });
  };

  const getRes = async (res: any) => {
    const { output, run_id } = res.return_values;
    let textValue = "";
    let img = "";
    switch (checkType(output)) {
      case "String":
        textValue = output;
        break;
      case "Array":
        textValue =
          output.find((item: any) => item.type === "text")?.text?.value || "";
        const file_id =
          output.find((item: any) => item.type === "image_file")?.image_file
            ?.file_id || "";
        if (file_id) {
          const imgRes = await fetchFiles(file_id);
          if (imgRes && imgRes.type === "data:image/png") {
            img = imgRes.image;
          }
        }
        break;
      default:
        textValue = output;
        break;
    }

    if (run_id) {
      const res = await fetchRuns(run_id);
      if (res && res.length > 0) {
        res.map((item: any) => {
          if (item.function_name === "get_usage") {
            saveChat(
              { res: item.res, text: textValue, img: img },
              "assistant",
              "chart"
            );
            return;
          }
          if (item.function_name === "get_plan") {
            saveChat(
              { res: item.res, address: item.raw_address, img: img },
              "assistant",
              "plan"
            );
            return;
          }
        });
      }
      if (
        !res ||
        res.length <= 0 ||
        res.every((item: any) => !item || item.function_name !== "get_usage")
      ) {
        saveChat({ text: textValue, img: img }, "assistant", "text");
      }
    } else {
      saveChat({ text: textValue, img: img }, "assistant", "text");
    }
  };

  return (
    <RecordInput>
      <WavePattern ref={micRef} show={`${isTalking}`} />
      {inputType === "message" && (
        <MessageInput>
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <MessageButtons right="18px">
            <Tooltip placement="top" title={"Use Microphone"} arrow>
              <InputIcon
                src={`${process.env.PUBLIC_URL}/images/icon-mic.png`}
                onClick={() => setInputType("voice")}
              />
            </Tooltip>
          </MessageButtons>
        </MessageInput>
      )}

      {inputType === "voice" && (
        <VoiceInput
          bgColor={isTalking ? "#A99DF5" : ""}
          disable={loading}
          onClick={() => {
            if (loading) return;
            isTalking ? destroyRecord() : openRecord();
            setIsCancel(false);
          }}
        >
          {isTalking && (
            <MessageButtons left="18px">
              <InputIcon
                src={`${process.env.PUBLIC_URL}/images/icon-delete.png`}
                onClick={(e) => {
                  setIsCancel(true);
                  destroyRecord();
                  setAudio(null);
                  e.stopPropagation();
                }}
                alt="Cancel"
              />
            </MessageButtons>
          )}

          <InputIcon
            src={
              isTalking
                ? `${process.env.PUBLIC_URL}/images/icon-stop.png`
                : `${process.env.PUBLIC_URL}/images/icon-mic.png`
            }
            style={{ marginRight: "10px" }}
          />
          <span>{isTalking ? "Stop To Talk" : "Click To Talk"}</span>
          <MessageButtons right="18px">
            <InputIcon
              src={`${process.env.PUBLIC_URL}/images/icon-keyboard.png`}
              onClick={(e) => {
                if (isTalking) return;
                setInputType("message");
                e.stopPropagation();
              }}
            />
          </MessageButtons>
        </VoiceInput>
      )}
    </RecordInput>
  );
});

export default Record;

const checkType = (input: any): string => {
  if (Array.isArray(input)) {
    return "Array";
  } else if (typeof input === "string") {
    return "String";
  } else {
    return "Unknown";
  }
};
